/**
 * PR-21 — Color palette system.
 * Each palette overrides a small set of CSS custom properties on :root.
 * Values are oklch / hex; they coexist with the project's design tokens.
 */
import { trackEvent } from "@/lib/audit";

export interface ThemePalette {
  id: string;
  label: string;
  flag?: string;
  primary: string;       // CSS color
  accent: string;
  background: string;
  foreground: string;
}

export const THEME_PALETTES: ThemePalette[] = [
  // === World Cup 2026 — Confederations ===
  // CONMEBOL
  { id: "argentina",      label: "Argentina",      flag: "🇦🇷", primary: "#74acdf", accent: "#f5d142", background: "#0c1422", foreground: "#f1f5ff" },
  { id: "brazil",         label: "Brasil",         flag: "🇧🇷", primary: "#009c3b", accent: "#ffdf00", background: "#0b1410", foreground: "#fff9e6" },
  { id: "uruguay",        label: "Uruguay",        flag: "🇺🇾", primary: "#0093dd", accent: "#fcd116", background: "#0a1422", foreground: "#f5fbff" },
  { id: "colombia",       label: "Colombia",       flag: "🇨🇴", primary: "#fcd116", accent: "#003893", background: "#1a1505", foreground: "#fffdf0" },
  { id: "ecuador",        label: "Ecuador",        flag: "🇪🇨", primary: "#ffd700", accent: "#003893", background: "#1a1505", foreground: "#fffdf0" },
  { id: "paraguay",       label: "Paraguay",       flag: "🇵🇾", primary: "#d52b1e", accent: "#0038a8", background: "#170a0a", foreground: "#fff5f5" },
  { id: "chile",          label: "Chile",          flag: "🇨🇱", primary: "#d52b1e", accent: "#0039a6", background: "#170a0a", foreground: "#fff5f5" },
  { id: "peru",           label: "Perú",           flag: "🇵🇪", primary: "#d91023", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "bolivia",        label: "Bolivia",        flag: "🇧🇴", primary: "#007a33", accent: "#ffe000", background: "#0b1410", foreground: "#fff9e6" },
  { id: "venezuela",      label: "Venezuela",      flag: "🇻🇪", primary: "#cf142b", accent: "#ffd700", background: "#170a0a", foreground: "#fff5f5" },
  // CONCACAF
  { id: "mexico",         label: "México",         flag: "🇲🇽", primary: "#006847", accent: "#ce1126", background: "#0b1410", foreground: "#f6fff8" },
  { id: "usa",            label: "Estados Unidos", flag: "🇺🇸", primary: "#3c3b6e", accent: "#b22234", background: "#0a0f1e", foreground: "#f4f6ff" },
  { id: "canada",         label: "Canadá",         flag: "🇨🇦", primary: "#ff0000", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "dominicana",     label: "Rep. Dominicana",flag: "🇩🇴", primary: "#002d62", accent: "#ce1126", background: "#0a0f1e", foreground: "#f5f8ff" },
  { id: "costa_rica",     label: "Costa Rica",     flag: "🇨🇷", primary: "#002b7f", accent: "#ce1126", background: "#0a0f1e", foreground: "#f5f8ff" },
  { id: "panama",         label: "Panamá",         flag: "🇵🇦", primary: "#0033a0", accent: "#d21034", background: "#0a0f1e", foreground: "#f5f8ff" },
  { id: "honduras",       label: "Honduras",       flag: "🇭🇳", primary: "#0073cf", accent: "#ffffff", background: "#0a0f1e", foreground: "#f5f8ff" },
  { id: "jamaica",        label: "Jamaica",        flag: "🇯🇲", primary: "#009b3a", accent: "#fed100", background: "#0b1410", foreground: "#fff9e6" },
  // UEFA
  { id: "spain",          label: "España",         flag: "🇪🇸", primary: "#aa151b", accent: "#f1bf00", background: "#1a0d0d", foreground: "#fff4e0" },
  { id: "france",         label: "Francia",        flag: "🇫🇷", primary: "#0055a4", accent: "#ef4135", background: "#0a0f1e", foreground: "#f5f8ff" },
  { id: "germany",        label: "Alemania",       flag: "🇩🇪", primary: "#0a0a0a", accent: "#dd0000", background: "#0a0a0a", foreground: "#f5f5f5" },
  { id: "italy",          label: "Italia",         flag: "🇮🇹", primary: "#0066cc", accent: "#009246", background: "#0a0f1e", foreground: "#f5f8ff" },
  { id: "england",        label: "Inglaterra",     flag: "🏴", primary: "#012169", accent: "#c8102e", background: "#0a0f1e", foreground: "#f5f8ff" },
  { id: "portugal",       label: "Portugal",       flag: "🇵🇹", primary: "#006600", accent: "#ff0000", background: "#0b1410", foreground: "#fff9e6" },
  { id: "netherlands",    label: "Países Bajos",   flag: "🇳🇱", primary: "#ff6600", accent: "#21468b", background: "#1a1408", foreground: "#fff8ee" },
  { id: "belgium",        label: "Bélgica",        flag: "🇧🇪", primary: "#fae042", accent: "#ed2939", background: "#1a1505", foreground: "#fffdf0" },
  { id: "croatia",        label: "Croacia",        flag: "🇭🇷", primary: "#ff0000", accent: "#171796", background: "#170a0a", foreground: "#fff5f5" },
  { id: "switzerland",    label: "Suiza",          flag: "🇨🇭", primary: "#da291c", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "denmark",        label: "Dinamarca",      flag: "🇩🇰", primary: "#c8102e", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "poland",         label: "Polonia",        flag: "🇵🇱", primary: "#dc143c", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "serbia",         label: "Serbia",         flag: "🇷🇸", primary: "#c6363c", accent: "#0c4076", background: "#170a0a", foreground: "#fff5f5" },
  { id: "austria",        label: "Austria",        flag: "🇦🇹", primary: "#ed2939", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "scotland",       label: "Escocia",        flag: "🏴", primary: "#0065bd", accent: "#ffffff", background: "#0a0f1e", foreground: "#f5f8ff" },
  { id: "wales",          label: "Gales",          flag: "🏴", primary: "#d30731", accent: "#00ab39", background: "#170a0a", foreground: "#fff5f5" },
  // AFC
  { id: "japan",          label: "Japón",          flag: "🇯🇵", primary: "#bc002d", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "south_korea",    label: "Corea del Sur",  flag: "🇰🇷", primary: "#cd2e3a", accent: "#0047a0", background: "#170a0a", foreground: "#fff5f5" },
  { id: "iran",           label: "Irán",           flag: "🇮🇷", primary: "#239f40", accent: "#da0000", background: "#0b1410", foreground: "#f6fff8" },
  { id: "saudi_arabia",   label: "Arabia Saudita", flag: "🇸🇦", primary: "#006c35", accent: "#ffffff", background: "#0b1410", foreground: "#f6fff8" },
  { id: "qatar",          label: "Qatar",          flag: "🇶🇦", primary: "#8a1538", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "australia",      label: "Australia",      flag: "🇦🇺", primary: "#00843d", accent: "#ffcd00", background: "#0b1410", foreground: "#fff9e6" },
  // CAF
  { id: "morocco",        label: "Marruecos",      flag: "🇲🇦", primary: "#c1272d", accent: "#006233", background: "#170a0a", foreground: "#fff5f5" },
  { id: "senegal",        label: "Senegal",        flag: "🇸🇳", primary: "#00853f", accent: "#fdef42", background: "#0b1410", foreground: "#fff9e6" },
  { id: "egypt",          label: "Egipto",         flag: "🇪🇬", primary: "#ce1126", accent: "#ffd700", background: "#170a0a", foreground: "#fff5f5" },
  { id: "nigeria",        label: "Nigeria",        flag: "🇳🇬", primary: "#008751", accent: "#ffffff", background: "#0b1410", foreground: "#f6fff8" },
  { id: "tunisia",        label: "Túnez",          flag: "🇹🇳", primary: "#e70013", accent: "#ffffff", background: "#170a0a", foreground: "#fff5f5" },
  { id: "ghana",          label: "Ghana",          flag: "🇬🇭", primary: "#cf0921", accent: "#ffd700", background: "#170a0a", foreground: "#fff5f5" },
  { id: "algeria",        label: "Argelia",        flag: "🇩🇿", primary: "#006233", accent: "#d21034", background: "#0b1410", foreground: "#f6fff8" },
  { id: "cote_divoire",   label: "Costa de Marfil",flag: "🇨🇮", primary: "#f77f00", accent: "#009e60", background: "#1a1408", foreground: "#fff8ee" },
  // OFC
  { id: "new_zealand",    label: "Nueva Zelanda",  flag: "🇳🇿", primary: "#0a0a0a", accent: "#cc142b", background: "#0a0a0a", foreground: "#f5f5f5" },
  // === Premium fallbacks ===
  { id: "dark_premium",   label: "VerseSport Dark",  primary: "#7c3aed", accent: "#f59e0b", background: "#0a0a0f", foreground: "#fafafa" },
  { id: "light_premium",  label: "VerseSport Light", primary: "#0ea5e9", accent: "#ec4899", background: "#fafafa", foreground: "#0a0a0a" },
];

const KEY = "active_theme_palette";

/** Convert "#rrggbb" to "r g b" so we can plug it into Tailwind/oklch color helpers. */
function hexToRgbTriplet(hex: string): string | null {
  const m = /^#?([a-f0-9]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/** Lighten a hex color by mixing with white for glow effects. */
function lighten(hex: string, amount = 0.35): string {
  const m = /^#?([a-f0-9]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.min(255, Math.round(((n >> 16) & 255) + 255 * amount));
  const g = Math.min(255, Math.round(((n >> 8) & 255) + 255 * amount));
  const b = Math.min(255, Math.round((n & 255) + 255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

export function applyThemePalette(id: string) {
  if (typeof document === "undefined") return;
  const palette =
    THEME_PALETTES.find((p) => p.id === id) ??
    THEME_PALETTES.find((p) => p.id === "dark_premium") ??
    THEME_PALETTES[0];
  const r = document.documentElement;
  r.style.setProperty("--palette-primary", palette.primary);
  r.style.setProperty("--palette-accent", palette.accent);
  r.style.setProperty("--palette-background", palette.background);
  r.style.setProperty("--palette-foreground", palette.foreground);

  // F5 — TEAM COLOR SYSTEM FIX: also drive the design-system tokens so
  // gradients, overlays, glows, buttons and confidence chips pick up the
  // selected team identity across the whole app.
  const glow = lighten(palette.primary, 0.4);
  r.style.setProperty("--primary-glow", glow);
  r.style.setProperty("--gradient-primary", `linear-gradient(135deg, ${palette.primary}, ${glow})`);
  const primaryRgb = hexToRgbTriplet(palette.primary);
  const accentRgb = hexToRgbTriplet(palette.accent);
  if (primaryRgb) r.style.setProperty("--team-primary-rgb", primaryRgb);
  if (accentRgb) r.style.setProperty("--team-accent-rgb", accentRgb);

  r.dataset.palette = palette.id;
  try { window.localStorage.setItem(KEY, palette.id); } catch { /* noop */ }
  void trackEvent("theme_selected", { palette: palette.id, label: palette.label });
}

export function loadThemePalette() {
  if (typeof window === "undefined") return;
  try {
    const id = window.localStorage.getItem(KEY);
    if (id) applyThemePalette(id);
  } catch { /* noop */ }
}

export function getActivePalette(): string {
  if (typeof window === "undefined") return "dark_premium";
  try { return window.localStorage.getItem(KEY) ?? "dark_premium"; } catch { return "dark_premium"; }
}